import React, { useEffect, useState, useMemo, createElement, useCallback } from "react";
import { Row, Col, Button, Card, Empty } from "antd";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { animated, to } from '@react-spring/web';
import { useTooltip } from '@nivo/tooltip';
import { useSelector } from "react-redux";
import SummaryCard from "./SummaryCard";
import SummaryGrid from "./SummaryGrid";
import { primaryShades, secondaryShades, variantTokens, greyScaleTokens } from "../../theme/theme";
import { formatNumberBritish } from "../../utils/numberFormatting";
import LoadingCard from "../Components/Cards/LoadingCard";

// Costanti dei colori dal tema
const PRIMARY = variantTokens.primary;
const PRIMARY_300 = primaryShades.primary300;
const PRIMARY_200 = primaryShades.primary200;

const SECONDARY = variantTokens.secondary;
const SECONDARY_200 = secondaryShades.secondary200;
const SECONDARY_100 = secondaryShades.secondary100;

const ACCENT = variantTokens.accent;

// Palette per i gradienti - utilizziamo le costanti del tema
const primaryLighter = PRIMARY_300;     // Versione più chiara del primary
const primaryLightest = PRIMARY_200;    // Versione ancora più chiara
const primaryVeryLight = greyScaleTokens.grey100; // Usato per sfumature quasi bianche
const secondaryLighter = SECONDARY_200; // Versione più chiara del secondary
const secondaryLightest = SECONDARY_100; // Versione ancora più chiara
const accentLighter = '#DAB4CD';       // Versione più chiara dell'accent
const accentLightest = '#E9D4E2';      // Versione ancora più chiara

// Custom bar component with only top corners rounded
const TopRoundedBar = (props) => {
    const {
        bar: { data, ...bar },
        style: {
            borderColor,
            color,
            height,
            labelColor,
            labelOpacity,
            labelX,
            labelY,
            transform,
            width,
        },
        borderRadius,
        borderWidth,
        label,
        shouldRenderLabel,
        isInteractive,
        onClick,
        onMouseEnter,
        onMouseLeave,
        tooltip,
        isFocusable,
        ariaLabel,
        ariaLabelledBy,
        ariaDescribedBy,
    } = props;

    const { theme } = props;
    const { showTooltipFromEvent, showTooltipAt, hideTooltip } = useTooltip();

    const renderTooltip = useMemo(
        () => () => createElement(tooltip, { ...bar, ...data }),
        [tooltip, bar, data]
    );
    const handleClick = useCallback(
        (event) => onClick && onClick({ color: bar.color, ...data }, event),
        [bar, data, onClick]
    );
    const handleTooltip = useCallback(
        (event) => showTooltipFromEvent(renderTooltip(), event),
        [showTooltipFromEvent, renderTooltip]
    );
    const handleMouseEnter = useCallback(
        (event) => {
            onMouseEnter && onMouseEnter(data, event);
            showTooltipFromEvent(renderTooltip(), event);
        },
        [data, onMouseEnter, showTooltipFromEvent, renderTooltip]
    );
    const handleMouseLeave = useCallback(
        (event) => {
            onMouseLeave && onMouseLeave(data, event);
            hideTooltip();
        },
        [data, hideTooltip, onMouseLeave]
    );
    const handleFocus = useCallback(
        () =>
            showTooltipAt(renderTooltip(), [
                bar.absX + bar.width / 2,
                bar.absY,
            ]),
        [showTooltipAt, renderTooltip, bar]
    );
    const handleBlur = useCallback(() => hideTooltip(), [hideTooltip]);

    return (
        <animated.g transform={transform}>
            <defs>
                <clipPath id={`round-corner-${label}`}>
                    <animated.rect
                        x={0}
                        y={0}
                        rx={borderRadius}
                        ry={borderRadius}
                        width={to(width, (v) => Math.max(v, 0))}
                        height={to(height, (v) => Math.max(v + borderRadius, 0))}
                    />
                </clipPath>
            </defs>
            <animated.rect
                clipPath={`url(#round-corner-${label})`}
                width={to(width, (v) => Math.max(v, 0))}
                height={to(height, (v) => Math.max(v, 0))}
                fill={data.fill ?? color}
                strokeWidth={borderWidth}
                stroke={borderColor}
                focusable={isFocusable}
                tabIndex={isFocusable ? 0 : undefined}
                aria-label={ariaLabel ? ariaLabel(data) : undefined}
                aria-labelledby={ariaLabelledBy ? ariaLabelledBy(data) : undefined}
                aria-describedby={ariaDescribedBy ? ariaDescribedBy(data) : undefined}
                onMouseEnter={
                    isInteractive ? handleMouseEnter : undefined
                }
                onMouseMove={isInteractive ? handleTooltip : undefined}
                onMouseLeave={
                    isInteractive ? handleMouseLeave : undefined
                }
                onClick={isInteractive ? handleClick : undefined}
                onFocus={
                    isInteractive && isFocusable ? handleFocus : undefined
                }
                onBlur={
                    isInteractive && isFocusable ? handleBlur : undefined
                }
            />
            {shouldRenderLabel && (
                <animated.text
                    x={labelX}
                    y={labelY}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fillOpacity={labelOpacity}
                    style={{
                        ...theme.labels.text,
                        pointerEvents: 'none',
                        fill: labelColor,
                    }}
                >
                    {label}
                </animated.text>
            )}
        </animated.g>
    );
};

function DeviceAnalytics({ aggregateList = [], loading, onResetFilters }) {
    const [devicesData, setDevicesData] = useState({});
    const [selectedTab, setSelectedTab] = useState("All Devices");
    const [filteredData, setFilteredData] = useState([]);
    const filterOptions = useSelector((state) => state.analytics.filterOptions);
    const selectedCatalog = useSelector((state) => state.catalogues.selectedCatalog);

    const mobileCategories = [
        "android",
        "android instant",
        "ios",
        "ios app clip",
        "fbav (mobile)",
        "instagram (mobile)",
        "samsungbrowser (mobile)",
        "chrome (mobile)",
        "safari (mobile)",
        "firefox (mobile)",
    ];

    const desktopCategories = [
        "safari (desktop)",
        "chrome (desktop)",
        "firefox (desktop)",
        "samsungbrowser (desktop)",
    ];

    // Filtra i dati dai dispositivi
    const DeviceFilter = (aggList) => {
        const deviceStats = { mobile: 0, desktop: 0, allDevice: 0, misc: [] };
        const deviceTypeSet = new Set();

        let allDevices = 0;

        aggList.forEach((item) => {
            // Usa list_device_data invece di deviceData
            const deviceData = item.list_device_data;

            if (deviceData) {
                deviceData.forEach((device) => {
                    const deviceType = device.type?.toLowerCase();
                    const count = device.count ?? 0;

                    // Evita duplicati usando Set
                    if (!deviceTypeSet.has(device.type)) {
                        deviceTypeSet.add(device.type);
                        deviceStats.misc.push({ name: device.type, count });
                    } else {
                        const index = deviceStats.misc.findIndex((el) => el.name === device.type);
                        deviceStats.misc[index].count += count;
                    }

                    // Classifica tra mobile e desktop
                    if (mobileCategories.some((cat) => deviceType?.includes(cat))) {
                        deviceStats.mobile += count;
                    } else if (desktopCategories.some((cat) => deviceType?.includes(cat))) {
                        deviceStats.desktop += count;
                    }

                    allDevices += count;
                });
            }
        });

        deviceStats.allDevice = allDevices;
        return deviceStats;
    };

    // Raggruppa e somma i dati per browser
    function groupAndJoinData(data) {
        if (!Array.isArray(data)) return []; // Verifica che 'data' sia un array valido

        const groupedData = new Map();

        data.forEach((item) => {
            const browserName = item?.name?.split(" ")[0]?.toLowerCase() ?? "unknown";
            if (!groupedData.has(browserName)) {
                groupedData.set(browserName, { name: browserName.toUpperCase(), count: 0 });
            }
            groupedData.get(browserName).count += item.count ?? 0;
        });

        return Array.from(groupedData.values()).sort((a, b) => b.count - a.count);
    }

    // Primo useEffect per calcolare i dati
    useEffect(() => {
        const deviceStats = DeviceFilter(aggregateList);

        if (!aggregateList || aggregateList.length === 0) {
            setDevicesData({});
            return;
        }

        setDevicesData(deviceStats);
    }, [aggregateList]);

    // Secondo useEffect: aggiorna in base alla scheda selezionata
    useEffect(() => {
        if (!devicesData?.misc || devicesData.misc.length === 0) {
            setFilteredData([]);
            return;
        }

        let filteredList = [];

        if (selectedTab === "Mobile Devices") {
            filteredList = devicesData.misc.filter((el) =>
                mobileCategories.some(cat => el.name?.toLowerCase()?.includes(cat))
            );
        } else if (selectedTab === "Desktop Devices") {
            filteredList = devicesData.misc.filter((el) =>
                desktopCategories.some(cat => el.name?.toLowerCase()?.includes(cat))
            );
        } else {
            filteredList = devicesData.misc;
        }

        const groupedData = groupAndJoinData(filteredList);
        // Trasformiamo i dati nel formato richiesto da Nivo
        const transformedData = groupedData
            .map(item => ({
                type: item.name,
                value: item.count
            }))

        setFilteredData(transformedData);
    }, [selectedTab, devicesData?.misc]);

    const handleTabChange = (tab) => {
        setSelectedTab(tab);
    };

    // Mappa per le schede
    const tabMap = useMemo(
        () => ({
            "All Devices": devicesData.allDevice,
            "Mobile Devices": devicesData.mobile,
            "Desktop Devices": devicesData.desktop,
        }),
        [devicesData]
    );

    const tooltipTextMap = {
        "All Devices": "The operating system in use by the users or the app version",
        "Mobile Devices": "The mobile operating system in use by the users or the app version",
        "Desktop Devices": "The desktop operating system in use by the users or the app version",
    };

    // Calcola il totale dei dispositivi per il centro del grafico
    const deviceTotal = useMemo(() => {
        if (!devicesData.mobile && !devicesData.desktop) return 0;
        return devicesData.allDevice || 0;
    }, [devicesData]);

    // Definizione dei gradienti per gli spicchi del grafico
    const defs = [
        {
            id: 'desktopGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: PRIMARY },
                { offset: 40, color: `${primaryLighter}DD` },
                { offset: 80, color: `${primaryLightest}CC` },
                { offset: 100, color: `${primaryVeryLight}BB` },
            ],
        },
        {
            id: 'mobileGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: SECONDARY },
                { offset: 40, color: `${secondaryLighter}DD` },
                { offset: 80, color: `${secondaryLightest}CC` },
                { offset: 100, color: `${secondaryLightest}BB` },
            ],
        },
        {
            id: 'tabletGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: ACCENT },
                { offset: 40, color: `${accentLighter}DD` },
                { offset: 80, color: `${accentLighter}CC` },
                { offset: 100, color: `${accentLightest}BB` },
            ],
        },
        {
            id: 'barGradient',
            type: 'linearGradient',
            colors: [
                { offset: 0, color: PRIMARY },
                { offset: 40, color: `${primaryLighter}DD` },
                { offset: 80, color: `${primaryLightest}CC` },
                { offset: 100, color: `${primaryVeryLight}BB` },
            ],
        },
    ];

    // Mappatura dei colori per gli spicchi
    const pieChartFill = [
        { match: { id: 'Desktop' }, id: 'desktopGradient' },
        { match: { id: 'Mobile' }, id: 'mobileGradient' },
    ];

    // Mappatura dei colori per le barre
    const barChartFill = [
        { match: '*', id: 'barGradient' }
    ];

    // Stile del tooltip personalizzato
    const tooltipStyle = {
        backgroundColor: 'white',
        padding: '8px 12px',
        border: '1px solid #e8e8e8',
        borderRadius: '2px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    };

    // Dati per il grafico a torta
    const pieChartData = useMemo(() => {
        if (!devicesData.mobile && !devicesData.desktop) return [];
        return [
            { id: 'Desktop', value: devicesData.desktop || 0 },
            { id: 'Mobile', value: devicesData.mobile || 0 }
        ];
    }, [devicesData]);

    return (
        <>
            <SummaryGrid>
                {Object.entries(tabMap).map(([key, value]) => {
                    // Calcola la percentuale per Mobile e Desktop Devices
                    let percentage = null;
                    if (key === "Mobile Devices" && devicesData.allDevice > 0) {
                        percentage = ((devicesData.mobile / devicesData.allDevice) * 100).toFixed(1);
                    } else if (key === "Desktop Devices" && devicesData.allDevice > 0) {
                        percentage = ((devicesData.desktop / devicesData.allDevice) * 100).toFixed(1);
                    }

                    return (
                        <SummaryCard
                            key={key}
                            label={key}
                            value={value !== undefined ? value : 0}
                            percentage={percentage}
                            loading={loading}
                            active={key === selectedTab}
                            onClick={() => handleTabChange(key)}
                            toolTipText={tooltipTextMap[key]}
                        />
                    );
                })}
            </SummaryGrid>

            {loading ? (
                <LoadingCard />
            ) : (
                <Row gutter={[16, 16]} justify="space-between">
                    {(filterOptions.line !== "" ||
                        filterOptions.variant !== "" ||
                        filterOptions.glass !== "" ||
                        selectedCatalog?.id !== "all") && (
                            <Col span={24} style={{ textAlign: 'right', marginBottom: '16px' }}>
                                <Button type="default" onClick={onResetFilters}>
                                    Clear filters
                                </Button>
                            </Col>
                        )}

                    {(!devicesData || !devicesData.misc || devicesData.misc.length === 0) ? (
                        <Col span={24}>
                            <Card
                                styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)' } }}
                            >
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data available" />
                            </Card>
                        </Col>
                    ) : (
                        <>
                            <Col xs={24} md={16}>
                                <Card
                                    styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
                                    style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
                                >
                                    <div style={{ height: '100%' }}>
                                        {filteredData && filteredData.length > 0 ? (
                                            <ResponsiveBar
                                                data={filteredData}
                                                keys={['value']}
                                                indexBy="type"
                                                margin={{ top: 20, right: 30, bottom: 60, left: 60 }}
                                                padding={0.7}
                                                innerPadding={4}
                                                minValue={0}
                                                maxValue={filteredData.length > 0 ? Math.max(...filteredData.map(d => d.value)) * 1.2 : 0}
                                                groupMode="grouped"
                                                layout="vertical"
                                                valueScale={{ type: 'linear' }}
                                                indexScale={{
                                                    type: 'band',
                                                    round: true,
                                                    padding: 0.5
                                                }}
                                                enableGridY={true}
                                                gridYValues={3}
                                                borderRadius={8}
                                                barComponent={TopRoundedBar}
                                                borderWidth={1}
                                                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                                defs={defs}
                                                fill={barChartFill}
                                                colors={[PRIMARY]}
                                                enableLabel={false}
                                                maxBarSize={20}
                                                axisBottom={{
                                                    tickSize: 5, tickPadding: 5, tickRotation: 0,
                                                    legend: '', legendPosition: 'middle', legendOffset: 32
                                                }}
                                                axisLeft={{
                                                    tickSize: 5, tickPadding: 5, tickRotation: 0,
                                                    legend: '', legendPosition: 'middle', legendOffset: -40,
                                                    tickValues: 3,
                                                    format: (value) => Math.round(value)
                                                }}
                                                tooltip={({ data, value, color, indexValue }) => {
                                                    const total = filteredData.reduce((sum, item) => sum + item.value, 0);
                                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                                    return (
                                                        <div style={tooltipStyle}>
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <div style={{
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    backgroundColor: color,
                                                                    marginRight: '8px'
                                                                }} />
                                                                <span style={{ fontWeight: 'bold' }}>{indexValue}:</span>
                                                                <span style={{ marginLeft: '4px' }}>
                                                                    {value} ({percentage}%)
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                                motionConfig="gentle"
                                                animate={true}
                                            />
                                        ) : (
                                            <Empty description="No device data available" />
                                        )}
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={24} md={8}>
                                <Card
                                    styles={{ body: { padding: 'clamp(0.75rem, 3vw, 1rem)', height: 'clamp(18.75rem, 50vh, 25rem)' } }}
                                    style={{ height: 'clamp(18.75rem, 50vh, 25rem)' }}
                                >
                                    {pieChartData && pieChartData.length > 0 ? (
                                        <div style={{ height: '100%', position: 'relative' }}>
                                            <ResponsivePie
                                                data={pieChartData}
                                                margin={{ top: 40, right: 20, bottom: 55, left: 20 }}
                                                innerRadius={0.85}
                                                padAngle={1}
                                                cornerRadius={8}
                                                activeOuterRadiusOffset={8}
                                                animate={true}
                                                startAngle={-150}
                                                endAngle={150}
                                                motionConfig="gentle"
                                                enableArcLabels={false}
                                                enableArcLinkLabels={true}
                                                arcLinkLabel={(d) => `${d.id} ${((d.value / deviceTotal) * 100).toFixed(1)}%`}
                                                arcLinkLabelsSkipAngle={10}
                                                arcLinkLabelsTextColor="#333"
                                                arcLinkLabelsThickness={2}
                                                arcLinkLabelsColor={{ from: 'color' }}
                                                arcLinkLabelsOffset={4}
                                                arcLinkLabelsDiagonalLength={16}
                                                arcLinkLabelsStraightLength={24}
                                                borderWidth={1}
                                                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                                defs={defs}
                                                fill={pieChartFill}
                                                colors={({ id }) => {
                                                    if (id === 'Desktop') return PRIMARY;
                                                    if (id === 'Mobile') return SECONDARY;
                                                    return '#cccccc'; // Colore di fallback
                                                }}
                                                enableSliceLabels={false}
                                                tooltip={({ datum }) => {
                                                    const percentage = ((datum.value / deviceTotal) * 100).toFixed(1);
                                                    return (
                                                        <div style={tooltipStyle}>
                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <div style={{
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    backgroundColor: datum.color,
                                                                    marginRight: '8px'
                                                                }} />
                                                                <span style={{ fontWeight: 'bold' }}>{datum.id}:</span>
                                                                <span style={{ marginLeft: '4px' }}>
                                                                    {datum.value} ({percentage}%)
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                                legends={[
                                                    {
                                                        anchor: 'bottom',
                                                        direction: 'row',
                                                        translateY: 56,
                                                        itemWidth: 80,
                                                        itemHeight: 18,
                                                        itemTextColor: '#333',
                                                        symbolSize: 12,
                                                        symbolShape: 'square',
                                                        effects: [
                                                            {
                                                                on: 'hover',
                                                                style: {
                                                                    itemTextColor: '#000'
                                                                }
                                                            }
                                                        ]
                                                    }
                                                ]}
                                            />
                                            <div style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                textAlign: 'center',
                                                pointerEvents: 'none',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                gap: '0rem',
                                                height: '40%'
                                            }}>
                                                <div style={{ fontSize: '3rem', fontWeight: 'bold', lineHeight: '1' }}>
                                                    {formatNumberBritish(deviceTotal)}
                                                </div>
                                                <div style={{ fontSize: '1rem', color: '#666', marginTop: '0.25rem' }}>
                                                    TOTAL
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Empty description="No device data available" />
                                    )}
                                </Card>
                            </Col>
                        </>
                    )}
                </Row>
            )}
        </>
    );
};

export default DeviceAnalytics;